import { useEffect, useId, useState } from "react";
import { Checkbox } from "@/components/ui/shadcn/checkbox";
import { useShow } from "@/contexts/ShowContext";

interface CharacterAssetsEditorProps {
  characters: any[];
  assets: any[];
  onSaved?: () => void;
  onCancel?: () => void;
  showActions?: boolean;
  cta?: string;
}

const assetCharacterIds = (asset: any): string[] => {
  if (!Array.isArray(asset?.characters)) return [];
  return asset.characters
    .map((c: any) => (c && typeof c === "object" ? c._id : c))
    .filter(Boolean);
};

export const CharacterAssetsEditor = ({
  characters,
  assets,
  onSaved,
  onCancel,
  showActions = true,
  cta = "Save",
}: CharacterAssetsEditorProps) => {
  const editorId = useId();

  const { handleAssignCharactersToAssets, characterSavingAssetIds } = useShow();
  const [touchedCharacterIds, setTouchedCharacterIds] = useState<string[]>([]);
  const [checkedCharacterIds, setCheckedCharacterIds] = useState<string[]>([]);

  const saving = assets.some((asset: any) =>
    characterSavingAssetIds.has(asset._id),
  );

  const assetIdsKey = assets.map((asset: any) => asset._id).join(",");

  const characterCheckState = (
    characterId: string,
  ): boolean | "indeterminate" => {
    if (touchedCharacterIds.includes(characterId))
      return checkedCharacterIds.includes(characterId);
    const withCharacter = assets.filter((asset: any) =>
      assetCharacterIds(asset).includes(characterId),
    ).length;
    if (withCharacter === 0) return false;
    if (withCharacter === assets.length) return true;
    return "indeterminate";
  };

  const toggleCharacter = (characterId: string, checked: boolean) => {
    setTouchedCharacterIds((prev) =>
      prev.includes(characterId) ? prev : [...prev, characterId],
    );
    setCheckedCharacterIds((prev) =>
      checked
        ? prev.includes(characterId)
          ? prev
          : [...prev, characterId]
        : prev.filter((id) => id !== characterId),
    );
  };

  const selectAll = () => {
    const allIds = characters.map((character) => character._id);
    setTouchedCharacterIds(allIds);
    setCheckedCharacterIds(allIds);
  };

  const unselectAll = () => {
    setTouchedCharacterIds(characters.map((character) => character._id));
    setCheckedCharacterIds([]);
  };

  const undo = () => {
    setTouchedCharacterIds([]);
    setCheckedCharacterIds([]);
  };

  const handleCancel = () => {
    undo();
    onCancel?.();
  };

  const handleSave = async () => {
    if (assets.length === 0) {
      return;
    }

    const add = touchedCharacterIds.filter((id) =>
      checkedCharacterIds.includes(id),
    );
    const remove = touchedCharacterIds.filter(
      (id) => !checkedCharacterIds.includes(id),
    );

    if (add.length === 0 && remove.length === 0) {
      onSaved?.();
      return;
    }

    await handleAssignCharactersToAssets(assets, { add, remove });
    onSaved?.();
  };

  useEffect(() => {
    setTouchedCharacterIds([]);
    setCheckedCharacterIds([]);
  }, [assetIdsKey]);

  const charactersAvailable = characters.length > 0;

  const isDirty = touchedCharacterIds.length > 0;

  const characterStates = characters.map((character) =>
    characterCheckState(character._id),
  );

  const allSelected: boolean | "indeterminate" = !charactersAvailable
    ? false
    : characterStates.every((state) => state === true)
      ? true
      : characterStates.every((state) => state === false)
        ? false
        : "indeterminate";

  const NoCharactersAvailable = () => {
    return (
      <span className="text-xs text-secondary-foreground/60">
        No characters available
      </span>
    );
  };

  const CharactersList = () => {
    return (
      <div className="flex flex-col gap-2 overflow-y-auto flex-1">
        {characters.map((character) => {
          const checked = characterCheckState(character._id);

          return (
            <div
              key={character._id}
              className="flex items-center gap-2 cursor-pointer"
            >
              <Checkbox
                id={`${editorId}-character-${character._id}`}
                className="CharacterAssetsEditor__checkbox"
                name={character._id}
                checked={checked}
                onCheckedChange={(checkedValue) =>
                  toggleCharacter(character._id, checkedValue === true)
                }
              />
              <label
                htmlFor={`${editorId}-character-${character._id}`}
                className="flex items-center gap-2 text-xs text-secondary-foreground select-none min-w-0"
              >
                {character.thumbnail_url && (
                  <img
                    src={character.thumbnail_url}
                    alt={character.name}
                    loading="lazy"
                    decoding="async"
                    className="w-5 h-5 rounded-full object-cover shrink-0"
                  />
                )}
                <span className="truncate">{character.name}</span>
              </label>
            </div>
          );
        })}
      </div>
    );
  };

  return (
    <div id={editorId} className="flex flex-col gap-2 h-full overflow-hidden">
      {charactersAvailable ? <CharactersList /> : <NoCharactersAvailable />}
      {charactersAvailable && (
        <>
          <div className="border-t border-border/60" />
          <div className="flex items-center gap-2 cursor-pointer">
            <Checkbox
              id={`${editorId}-character-all`}
              className="CharacterAssetsEditor__checkbox"
              checked={allSelected}
              onCheckedChange={(checkedValue) =>
                checkedValue === true ? selectAll() : unselectAll()
              }
            />
            <label
              htmlFor={`${editorId}-character-all`}
              className="text-xs font-medium text-secondary-foreground select-none"
            >
              All
            </label>
          </div>
        </>
      )}
      {showActions && (
        <div className="flex flex-col gap-1.5">
          {charactersAvailable && (
            <div className="flex items-center gap-2">
              <button
                type="button"
                className="inline-flex h-8 flex-1 items-center justify-center rounded-md bg-primary px-3 text-xs font-medium text-primary-foreground hover:bg-primary/90 cursor-pointer disabled:opacity-50 disabled:cursor-default"
                onClick={handleSave}
                disabled={saving}
              >
                {saving ? "Saving…" : cta}
              </button>
              {onCancel && (
                <button
                  type="button"
                  className="inline-flex h-8 items-center justify-center rounded-md border bg-background px-3 text-xs font-medium text-secondary-foreground hover:bg-secondary cursor-pointer disabled:opacity-50 disabled:cursor-default"
                  onClick={handleCancel}
                  disabled={saving}
                >
                  Cancel
                </button>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
};
